import React from 'react'
import NumberRangeCheckboxInput from '../inputs/number-range-checkbox-input'
import { InputGroupProps } from '../inputs/input-group'
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form'

/**
 * Props for the FormNumberRangeInput component
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 */
type FormNumberRangeInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = {
    /** Additional CSS classes for the options wrapper */
    optionsWrapperClassName?: string,
    /** Optional callback for value changes */
    onChange?: (value: string) => void,
    /** Props to pass to the InputGroup wrapper component */
    inputGroupProps?: InputGroupProps,
    /** The field name in the form (must be a valid path in TFieldValues) */
    name: TName,
    /** React Hook Form control object */
    control: Control<TFieldValues>
}

/**
 * A form number range input component that integrates with React Hook Form
 * 
 * This component renders a number range selector using checkboxes.
 * It wraps the NumberRangeCheckboxInput component with form integration.
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 * 
 * @param props - Component props
 * @param props.optionsWrapperClassName - CSS classes for options wrapper
 * @param props.onChange - Optional change callback
 * @param props.inputGroupProps - Props for the InputGroup wrapper
 * @param props.name - The field name in the form
 * @param props.control - React Hook Form control object
 * 
 * @example
 * ```tsx
 * // In your form component
 * const form = useForm<MyFormType>()
 * 
 * <FormNumberRangeInput
 *   name="priceRange"
 *   control={form.control}
 *   inputGroupProps={{
 *     label: "Price Range",
 *   }}
 * />
 * ```
 * 
 * @throws {Error} When the name prop is not provided
 * 
 * @returns JSX.Element - The rendered number range input component
 */
function FormNumberRangeInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
    inputGroupProps, 
    ...props 
}: FormNumberRangeInputProps<TFieldValues, TName>) {
    const { name, control } = props
    
    // Validate that name is provided as it's required for form integration
    if(!name) {
        throw new Error('name is required')
    }
    
    // Controller for form field integration
    const { field, fieldState } = useController({
        control,
        name,
    })
    const { error } = fieldState
    
    return (
        <NumberRangeCheckboxInput 
            {...props}
            {...field}
            {...inputGroupProps}
            error={error?.message}
        />
    )
}

export default FormNumberRangeInput